import type { Identity } from '../types/auth';

/** Tesseract staff team labels (case-sensitive). */
export const TESSERACT_TEAM_BUILD = 'buildteam';
export const TESSERACT_TEAM_EVENT = 'eventteam';
export const TESSERACT_TEAM_NEWSLETTER = 'Newsletter';
export const TESSERACT_TEAM_CURATORS = 'Curators';

export type LimitedTesseractTeamRole =
  | typeof TESSERACT_TEAM_BUILD
  | typeof TESSERACT_TEAM_EVENT
  | typeof TESSERACT_TEAM_NEWSLETTER
  | typeof TESSERACT_TEAM_CURATORS;

export type LimitedTesseractTeamAccess = {
  role: LimitedTesseractTeamRole;
  /** Calendar read-only (Newsletter-only); no workspaces / build unless granted elsewhere. */
  calendarViewOnly: boolean;
};

const LIMITED_ROLES: readonly LimitedTesseractTeamRole[] = [
  TESSERACT_TEAM_BUILD,
  TESSERACT_TEAM_EVENT,
  TESSERACT_TEAM_NEWSLETTER,
  TESSERACT_TEAM_CURATORS,
] as const;

function normalizeStaffTeams(staffTeams: unknown): string[] {
  if (!Array.isArray(staffTeams)) return [];
  return staffTeams.filter((t): t is string => typeof t === 'string' && t.trim() !== '');
}

function hasOtherExplicitAccess(identity: Identity): boolean {
  const groups = identity.raw?.custom?.groups;
  const permissions = identity.raw?.custom?.permissions;
  const g = Array.isArray(groups) ? groups : [];
  const p = Array.isArray(permissions) ? permissions : [];
  if (g.length > 0) return true;
  return p.length > 0;
}

/**
 * Users who appear only on one of the named Tesseract teams and have no JWT groups or
 * permission strings get a minimal Argus surface (navigation and route guards match).
 */
export function getLimitedTesseractTeamAccess(
  identity: Identity | null,
  isDev: boolean,
): LimitedTesseractTeamAccess | null {
  if (isDev || !identity) return null;
  if (hasOtherExplicitAccess(identity)) return null;

  const teams = normalizeStaffTeams(identity.raw?.custom?.staff_teams);
  if (teams.length !== 1) return null;

  const only = teams[0]!;
  if (!LIMITED_ROLES.includes(only as LimitedTesseractTeamRole)) return null;

  const role = only as LimitedTesseractTeamRole;
  return {
    role,
    calendarViewOnly: role === TESSERACT_TEAM_NEWSLETTER,
  };
}

const SCOPED_TEAM_BY_KEY: ReadonlyMap<string, LimitedTesseractTeamRole> = new Map(
  LIMITED_ROLES.map((role) => [role.toLowerCase(), role]),
);

export type TeamScopedStaff = {
  /** The user's staff teams, every one of them a team-scoped team (build, event, newsletter, curators). */
  teams: LimitedTesseractTeamRole[];
};

/**
 * Staff whose only staff teams are team-scoped ones (Build Team, Event Team, Newsletter,
 * Curators) see the dashboard and their own team panels, never the general staff tooling
 * (player data, logs, economy, infrastructure...). Unlike getLimitedTesseractTeamAccess this
 * holds whatever groups the user carries — a builder with `argus_build_panel` is still only a
 * builder — so the panel groups keep working while everything gated on "any staff member" hides.
 * A second, non-scoped staff team (moderation, admin, dev...) or SPY_Super_admin lifts it.
 * The backend applies the same rule to game data (src/auth.ts getTeamScopedStaffTeams).
 */
export function getTeamScopedStaff(identity: Identity | null, isDev: boolean): TeamScopedStaff | null {
  if (isDev || !identity) return null;
  const groups = identity.raw?.custom?.groups;
  if (Array.isArray(groups) && groups.includes('SPY_Super_admin')) return null;

  const teams = normalizeStaffTeams(identity.raw?.custom?.staff_teams);
  if (teams.length === 0) return null;

  const scoped: LimitedTesseractTeamRole[] = [];
  for (const team of teams) {
    const role = SCOPED_TEAM_BY_KEY.get(team.trim().toLowerCase());
    if (!role) return null;
    if (!scoped.includes(role)) scoped.push(role);
  }
  return { teams: scoped };
}

export function isCalendarViewOnlyTesseractUser(
  identity: Identity | null,
  isDev: boolean,
): boolean {
  return getLimitedTesseractTeamAccess(identity, isDev)?.calendarViewOnly === true;
}

/** Implied read access to the staff member's kanban team when they have no kanban JWT scopes. */
export function teamStaffOnlyKanbanRead(
  identity: Identity | null,
  isDev: boolean,
  team: string | null | undefined,
): boolean {
  if (!team) return false;
  const limited = getLimitedTesseractTeamAccess(identity, isDev);
  if (!limited || limited.calendarViewOnly) return false;
  return limited.role === team;
}

export function teamStaffOnlyKanbanWrite(
  identity: Identity | null,
  isDev: boolean,
  team: string | null | undefined,
): boolean {
  if (!team) return false;
  const limited = getLimitedTesseractTeamAccess(identity, isDev);
  if (!limited || limited.calendarViewOnly) return false;
  return limited.role === team && (limited.role === TESSERACT_TEAM_BUILD || limited.role === TESSERACT_TEAM_EVENT);
}
